import React from "react";
import { useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
// `Plus`, `Trash` and `UserPlus` all exist in the pinned lucide-react 0.336.0 —
// `UserRoundPlus` does NOT (it landed in 0.344). Do not reach for it.
import { Plus, Trash, UserPlus } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { SortableList } from "@/components/ui/auto-form/fields/sortable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePeopleRepository } from "@/pages/people/hooks/use-people-repository";
import { PlayCastNestedType } from "@/pages/plays/schemas/play.nested.form";

type PersonOption = {
  id: string;
  name: string;
  knownForDepartment?: string;
  media?: { photos?: Array<{ url?: string; blurhash?: string }> };
};

/**
 * The seven departments a play credit can carry. Unlike `PERSON_DEPARTMENTS` on the
 * person-resolution HITL panel — which stays at Tina's four-value `PERSON_TARGET_DEPARTMENTS`
 * resolution vocabulary — a play credit's `department` has no gate on Tina's side: the
 * backend's `cast[].department` is a free string (`PlayCastDbDto`), so this list can hold all
 * seven without waiting on any pipeline change.
 */
const PLAY_DEPARTMENTS = [
  "Acting",
  "Directing",
  "Writing",
  "Translating",
  "Design",
  "Music",
  "Choreography",
];

/**
 * Cast & crew editor for a play. Each entry is either *linked* to a Person or a
 * *name-only* credit — the backend's exactly-one-of rule.
 *
 * Unlike the movie editor, the name-only row is the expected case, not the fallback:
 * decision D1 means Tina creates no person doc for a stage-only credit, so most rows here
 * are free text and that is correct data, not missing data.
 */
export function PlayCastField({ id, inputProps }: AutoFormFieldProps) {
  const { search } = usePeopleRepository();
  const value: PlayCastNestedType[] = useWatch({ name: id }) ?? [];

  const setValue = (next: PlayCastNestedType[]) => {
    inputProps.onChange({
      target: { name: id, value: next },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const patch = (index: number, partial: Partial<PlayCastNestedType>) =>
    setValue(value.map((c, i) => (i === index ? { ...c, ...partial } : c)));

  const addLinked = (person: PersonOption | undefined) => {
    if (!person?.id) return;
    if (value.some((c) => c.personId === person.id)) return;
    setValue([
      ...value,
      {
        personId: person.id,
        name: person.name,
        characterName: null,
        department: person.knownForDepartment || "Acting",
        photo: person.media?.photos?.[0]?.url
          ? {
              url: person.media.photos[0].url!,
              blurhash: person.media.photos[0].blurhash ?? "",
            }
          : null,
      },
    ]);
  };

  const addNameOnly = () =>
    setValue([
      ...value,
      { personId: null, name: "", characterName: null, department: "Acting" },
    ]);

  return (
    <div className="flex w-full flex-col space-y-3 text-foreground">
      <Button
        type="button"
        variant="default"
        className="flex w-fit items-center"
        onClick={addNameOnly}
      >
        <Plus className="mr-2" size={16} />
        Add credit without a person
      </Button>

      <RemoteSelect<PersonOption>
        className="w-[500px]"
        triggerType="change"
        onValueChanged={addLinked}
        accessorFn={(option) => ({
          id: option?.id || "",
          name: option?.name ?? "",
          iconUrl: option?.media?.photos?.[0]?.url,
          number: option?.knownForDepartment,
        })}
        onSearch={async (query, onStatusChanged) => {
          onStatusChanged("loading");
          const result = (await search(query)) as PersonOption[];
          onStatusChanged("success");
          return result;
        }}
      />

      <SortableList
        handle
        orientation="vertical"
        items={value.map((c, i) => ({
          ...c,
          id: c.personId ?? `name-only-${i}-${c.name ?? ""}`,
        }))}
        render={(item, index) => (
          <div className="flex w-full flex-row items-center gap-2">
            {item.photo?.url ? (
              <PhotoWithBlurhash
                className="size-10 shrink-0 rounded-full object-cover"
                url={item.photo.url}
                blurhash={item.photo.blurhash ?? ""}
              />
            ) : (
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <UserPlus className="size-4" />
              </div>
            )}

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              {item.personId ? (
                <span className="flex items-center gap-2 truncate text-sm font-medium">
                  {item.name}
                  <Badge variant="secondary" className="text-[10px]">
                    linked
                  </Badge>
                </span>
              ) : (
                <div className="flex flex-row gap-1">
                  <Input
                    className="h-8"
                    placeholder="Name as printed on the bill"
                    defaultValue={item.name ?? ""}
                    onBlur={(e) => patch(index!, { name: e.target.value })}
                  />
                  <Input
                    className="h-8 w-14 shrink-0"
                    aria-label="Name language"
                    placeholder="el"
                    defaultValue={item.nameLocale ?? ""}
                    onBlur={(e) =>
                      patch(index!, {
                        nameLocale: e.target.value.trim().toLowerCase() || null,
                      })
                    }
                  />
                  <Input
                    className="h-8"
                    aria-label="Name in Latin letters"
                    placeholder="Latin spelling (if not Latin)"
                    defaultValue={item.latinName ?? ""}
                    onBlur={(e) =>
                      patch(index!, {
                        latinName: e.target.value.trim() || null,
                      })
                    }
                  />
                </div>
              )}
            </div>

            <div className="w-40 shrink-0">
              <Input
                className="h-8"
                placeholder="Role / character"
                defaultValue={item.characterName ?? ""}
                onBlur={(e) =>
                  patch(index!, { characterName: e.target.value || null })
                }
              />
            </div>

            <div className="w-32 shrink-0">
              <Input
                className="h-8"
                list="play-cast-departments"
                placeholder="Department"
                defaultValue={item.department ?? "Acting"}
                onBlur={(e) =>
                  patch(index!, { department: e.target.value || "Acting" })
                }
              />
            </div>

            <Button
              variant="secondary"
              size="icon"
              type="button"
              aria-label="Remove credit"
              onClick={() => setValue(value.filter((_, i) => i !== index))}
            >
              <Trash className="size-4" />
            </Button>
          </div>
        )}
        onChanged={(items) =>
          setValue(items.map(({ id: _id, ...rest }: any) => rest))
        }
      />

      <datalist id="play-cast-departments">
        {PLAY_DEPARTMENTS.map((d) => (
          <option key={d} value={d} />
        ))}
      </datalist>
    </div>
  );
}
